import { useState } from 'react'
import { useTheme } from '@/ui/theme'
import { addToast, Button, Tab, Tabs } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useTranslate } from '@/hooks'
import { parseUrlEmail } from '@/utils'
import { PortalFooter } from '@/ui/layout/portal-footer'
import { BankIcon } from '@/ui/icons/bank'
import { GlobeIcon } from '@/ui/icons/globe'
import { CheckIcon } from '@/ui/icons/check'
import {
  IbanForm,
  IbanFormData
} from '@/app/confirmation/bank-transfer/iban-form'
import {
  InternationalForm,
  InternationalFormData
} from '@/app/confirmation/bank-transfer/international-form'
import { useChangeToBankTransfer } from '@/hooks/use-change-to-bank-transfer'

const ChangeCompensationMethod = () => {
  const { translate } = useTranslate()
  const { t } = useTranslation()
  const theme = useTheme()

  const [searchParams] = useSearchParams()

  const emailParam = searchParams.get('email')
  const processId = searchParams.get('process_id') ?? ''
  const email = emailParam ? parseUrlEmail(emailParam) : ''

  const [accountTail, setAccountTail] = useState('')

  const {
    mutate: changeToBankTransfer,
    isPending: isLoading,
    isSuccess
  } = useChangeToBankTransfer()

  const onSubmit = (data: IbanFormData | InternationalFormData) => {
    const partial = data as Partial<IbanFormData & InternationalFormData>

    const rawAccount = (partial.iban || partial.bban || '').replace(/\s+/g, '')
    setAccountTail(rawAccount.slice(-4))

    changeToBankTransfer(
      {
        processId,
        email,
        iban: partial.iban,
        swift: partial.swift,
        bban: partial.bban,
        bankAccountCountry: partial.country_code
      },
      {
        onError: () => {
          addToast({
            title: t('change_compensation_method.error_toast_title'),
            description: t(
              'change_compensation_method.error_toast_description'
            ),
            color: 'danger'
          })
        }
      }
    )
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-4">
      <div className="flex flex-1 flex-col items-center justify-center space-y-4">
        {isSuccess ? (
          <div className="flex max-w-md flex-col items-center gap-6 text-center">
            <span className="flex size-12 items-center justify-center rounded-full bg-success-light text-success-dark">
              <CheckIcon width={30} height={30} />
            </span>
            <h1
              className="text-xl font-semibold leading-tight [text-wrap:balance]"
              dangerouslySetInnerHTML={{
                __html: translate('change_compensation_method_page_success')
              }}
            />
            {accountTail ? (
              <p className="text-sm leading-relaxed text-neutral-600 [text-wrap:pretty]">
                {t('change_compensation_method.success_account_ending', {
                  last4: accountTail
                })}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-8">
            <div className="max-w-md space-y-4 text-center">
              <h4
                className="text-2xl [text-wrap:balance]"
                dangerouslySetInnerHTML={{
                  __html: translate('change_compensation_method_page_title')
                }}
              />
              <p
                className="[text-wrap:pretty]"
                dangerouslySetInnerHTML={{
                  __html: translate(
                    'change_compensation_method_page_description'
                  )
                }}
              />
            </div>
            <div className="mx-auto w-full max-w-sm">
              <Tabs
                aria-label="Options"
                size="md"
                radius="sm"
                fullWidth
                classNames={{
                  base: 'w-full',
                  tabList: 'w-full rounded-md',
                  tab: 'data-selected:font-semibold truncate',
                  cursor: '!bg-white',
                  panel: '!mt-2 px-0'
                }}
              >
                <Tab
                  key="iban"
                  title={
                    <div className="flex items-center gap-2">
                      <BankIcon className="size-4" />
                      {translate('confirmation_page_bank_transfer_iban_title')}
                    </div>
                  }
                >
                  <IbanForm onSubmit={onSubmit} />
                </Tab>
                <Tab
                  key="international"
                  title={
                    <div className="flex items-center gap-2">
                      <GlobeIcon className="size-4" />
                      {translate(
                        'confirmation_page_bank_transfer_international_title'
                      )}
                    </div>
                  }
                >
                  <InternationalForm onSubmit={onSubmit} />
                </Tab>
              </Tabs>
              <Button
                isLoading={isLoading}
                fullWidth
                radius="sm"
                size="lg"
                form="bank-transfer-form"
                type="submit"
                className="mt-4 text-sm"
                style={{
                  backgroundColor: theme.colors.primary.main,
                  color: theme.colors.primary.contrastText,
                  borderRadius: theme.borderRadius
                }}
              >
                {translate('iban_button_complete')}
              </Button>
            </div>
          </div>
        )}
      </div>
      <PortalFooter />
    </div>
  )
}

export default ChangeCompensationMethod
